/**
 * Phone Validation Schemas for AmalQ
 * Comprehensive validation for phone numbers and verification codes
 * Following enterprise-grade patterns from existing codebase
 */

import { z } from 'zod';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Phone number validation schema with helpful error messages
 * Validates international phone numbers using libphonenumber-js
 */
export const phoneSchema = z.string()
  .min(1, 'Phone number is required')
  .refine(
    (val) => isValidPhoneNumber(val),
    { 
      message: 'Please enter a valid phone number with country code (e.g., +1 555-123-4567)' 
    }
  );

/**
 * Verification code validation schema (6 digits)
 * Ensures code is exactly 6 numeric digits
 */
export const phoneCodeSchema = z.string()
  .length(6, 'Verification code must be exactly 6 digits')
  .regex(/^\d+$/, 'Code must contain only numbers');

/**
 * Phone number change request schema
 * Validates new phone number for change operations
 */
export const changePhoneSchema = z.object({
  newPhoneNumber: phoneSchema,
});

/**
 * Phone verification request schema
 * Validates phone number and verification code
 */
export const verifyPhoneSchema = z.object({
  phoneNumber: phoneSchema,
  code: phoneCodeSchema,
});

/**
 * Password reset request schema (enhanced with phone support)
 * Supports both email and phone identifiers
 */
export const passwordResetRequestSchema = z.object({
  identifier: z.string().min(1, 'Email or phone number is required'),
  identifierType: z.enum(['email', 'phone'], {
    error: 'Must be either email or phone',
  }),
});

/**
 * Password reset verification schema
 * Validates reset code with identifier
 */
export const passwordResetVerifySchema = z.object({
  identifier: z.string().min(1, 'Email or phone number is required'),
  identifierType: z.enum(['email', 'phone']),
  code: phoneCodeSchema,
});

/**
 * Password reset completion schema
 * Validates new password with verified code
 */
export const passwordResetCompleteSchema = z.object({
  identifier: z.string().min(1, 'Email or phone number is required'),
  identifierType: z.enum(['email', 'phone']),
  code: phoneCodeSchema,
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be less than 128 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one lowercase letter, one uppercase letter, and one number'),
});

/**
 * E.164 format normalization helper
 * Converts phone number to international E.164 format
 * 
 * @param phone - Phone number string to normalize
 * @returns Normalized phone number in E.164 format or original if invalid
 * 
 * @example
 * normalizePhoneNumber('+1 (555) 123-4567') // '+15551234567'
 * normalizePhoneNumber('555-123-4567') // '555-123-4567' (unchanged - no country code)
 */
export const normalizePhoneNumber = (phone: string): string => {
  try {
    const parsed = parsePhoneNumberFromString(phone);
    return parsed?.format('E.164') || phone;
  } catch (error) {
    // Return original if parsing fails
    return phone;
  }
};

/**
 * Format phone number for display
 * Converts E.164 format to user-friendly display format
 * 
 * @param phone - Phone number in E.164 format
 * @returns Formatted phone number for display
 * 
 * @example
 * formatPhoneForDisplay('+15551234567') // '+1 (555) 123-4567'
 */
export const formatPhoneForDisplay = (phone: string): string => {
  try {
    const parsed = parsePhoneNumberFromString(phone);
    return parsed?.formatNational() || phone;
  } catch (error) {
    return phone;
  }
};

/**
 * Validate phone number format without throwing
 * Safe validation that returns boolean result
 * 
 * @param phone - Phone number to validate
 * @returns True if valid, false otherwise
 */
export const isValidPhoneNumberSafe = (phone: string): boolean => {
  try {
    return isValidPhoneNumber(phone);
  } catch (error) {
    return false;
  }
};

/**
 * Get country code from phone number
 * Extracts country code from international phone number
 * 
 * @param phone - Phone number to extract country from
 * @returns Country code (ISO 3166-1 alpha-2) or null if invalid
 * 
 * @example
 * getCountryFromPhone('+15551234567') // 'US'
 * getCountryFromPhone('+447700900123') // 'GB'
 */
export const getCountryFromPhone = (phone: string): string | null => {
  try {
    const parsed = parsePhoneNumberFromString(phone);
    return parsed?.country || null;
  } catch (error) {
    return null;
  }
};

/**
 * Type definitions for phone validation
 */
export type PhoneValidationResult = {
  isValid: boolean;
  error?: string;
  normalized?: string;
  country?: string;
};

/**
 * Validate phone number and return detailed result
 * Comprehensive validation with detailed error information
 * 
 * @param phone - Phone number to validate
 * @returns Detailed validation result
 */
export const validatePhoneNumber = (phone: string): PhoneValidationResult => {
  try {
    if (!phone || phone.trim() === '') {
      return {
        isValid: false,
        error: 'Phone number is required'
      };
    }

    const isValid = isValidPhoneNumber(phone);
    if (!isValid) {
      return {
        isValid: false,
        error: 'Please enter a valid phone number with country code (e.g., +1 555-123-4567)'
      };
    }

    const parsed = parsePhoneNumberFromString(phone);
    return {
      isValid: true,
      normalized: parsed?.format('E.164'),
      country: parsed?.country || undefined
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Invalid phone number format'
    };
  }
};
